import { lazy, Suspense, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router";
import { RouteLoading } from "@/components/layout/RouteLoading";
import { getPageMetadata } from "@/config/routes";
import { syncDocumentMetadata } from "@/lib/seo";

const ContactPage = lazy(async () => ({
  default: (await import("@/pages/ContactPage")).ContactPage,
}));
const DashboardPage = lazy(async () => ({
  default: (await import("@/pages/DashboardPage")).DashboardPage,
}));
const HomePage = lazy(async () => ({
  default: (await import("@/pages/HomePage")).HomePage,
}));
const NotFoundPage = lazy(async () => ({
  default: (await import("@/pages/NotFoundPage")).NotFoundPage,
}));
const PrivacyPage = lazy(async () => ({
  default: (await import("@/pages/PrivacyPage")).PrivacyPage,
}));
const ServicesPage = lazy(async () => ({
  default: (await import("@/pages/ServicesPage")).ServicesPage,
}));
const AboutPage = lazy(async () => ({
  default: (await import("@/pages/AboutPage")).AboutPage,
}));
const FaqPage = lazy(async () => ({
  default: (await import("@/pages/FaqPage")).FaqPage,
}));
const BlogPage = lazy(async () => ({
  default: (await import("@/pages/BlogPage")).BlogPage,
}));
const ArticlePage = lazy(async () => ({
  default: (await import("@/pages/ArticlePage")).ArticlePage,
}));
const StyleGuidePage = lazy(async () => ({
  default: (await import("@/pages/StyleGuidePage")).StyleGuidePage,
}));

function DocumentMetadata() {
  const location = useLocation();

  useEffect(() => {
    syncDocumentMetadata(getPageMetadata(location.pathname));
  }, [location.pathname]);

  return null;
}

function ScrollToHash() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    let attempts = 0;
    let frame = 0;
    const tick = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ block: "start" });
        return;
      }
      attempts += 1;
      if (attempts < 30) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [location.pathname, location.hash]);

  return null;
}

export function App() {
  return (
    <>
      <DocumentMetadata />
      <ScrollToHash />
      <Suspense fallback={<RouteLoading />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<ArticlePage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/style-guide" element={<StyleGuidePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/404" element={<NotFoundPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  );
}
